
import { useState, useEffect } from 'react';
import { BusinessDetails, FormSection } from '../types';
// Update to use standardized validation utilities that return string | undefined
import { 
  validatePhone, 
  validateEmail 
} from '@/components/portfolio/settings/utils/validationUtils';

export const useFormSections = (businessDetails: BusinessDetails) => {
  const [formSection, setFormSection] = useState<FormSection>('search');
  const [basicInfoComplete, setBasicInfoComplete] = useState(false);
  const [addressComplete, setAddressComplete] = useState(false);
  const [industryComplete, setIndustryComplete] = useState(false);
  const [basicInfoEdited, setBasicInfoEdited] = useState(false);
  const [addressEdited, setAddressEdited] = useState(false);
  const [industryEdited, setIndustryEdited] = useState(false);
  
  // Add state to track unsaved changes
  const [previousBusinessDetails, setPreviousBusinessDetails] = useState<BusinessDetails>({...businessDetails});
  const [basicInfoHasChanges, setBasicInfoHasChanges] = useState(false);
  const [addressHasChanges, setAddressHasChanges] = useState(false);
  const [industryHasChanges, setIndustryHasChanges] = useState(false);
  
  // Check if basic info is complete
  useEffect(() => {
    // validatePhone from validationUtils returns undefined if valid, or an error string.
    // For isPhoneValid to be true, validatePhone must return undefined.
    // If businessDetails.phone is empty, we consider it "valid" for this check,
    // as the !!businessDetails.phone check below handles requirement.
    const isPhoneValid = businessDetails.phone ? validatePhone(businessDetails.phone) === undefined : true;
    
    // NOTE: validateEmail is imported but not used in this useEffect for completion status.
    // If it were, similar logic would apply:
    // const isEmailValid = businessDetails.email ? validateEmail(businessDetails.email) === undefined : true;

    // Enhanced validation for basic info
    setBasicInfoComplete(
      !!businessDetails.name && 
      businessDetails.name.trim() !== '' && 
      !!businessDetails.phone && // Ensures phone is present
      isPhoneValid &&            // Ensures phone format is valid if present
      !!businessDetails.website && 
      businessDetails.website.trim() !== ''
      // Make founded optional
    );
    
    setAddressComplete(
      !!businessDetails.address && 
      businessDetails.address.trim() !== '' && 
      !!businessDetails.city && 
      businessDetails.city.trim() !== '' && 
      !!businessDetails.state && 
      businessDetails.state.trim() !== '' && 
      !!businessDetails.zip && 
      businessDetails.zip.trim() !== '' &&
      !!businessDetails.timeZone
    );
    
    setIndustryComplete(
      !!businessDetails.industry && 
      businessDetails.industry.trim() !== ''
    );
    
    console.log('Basic info complete:', basicInfoComplete);
    console.log('Business details:', businessDetails);
  }, [businessDetails]);
  
  // Track changes to businessDetails to detect unsaved changes
  useEffect(() => {
    if (formSection !== 'search') {
      // Basic info section changes
      const basicInfoFields = ['name', 'shortName', 'phone', 'email', 'website', 'founded', 'timeZone'];
      setBasicInfoHasChanges(basicInfoFields.some(field => 
        businessDetails[field as keyof BusinessDetails] !== previousBusinessDetails[field as keyof BusinessDetails]
      ));
      
      // Address section changes
      const addressFields = ['address', 'city', 'state', 'zip'];
      setAddressHasChanges(addressFields.some(field => 
        businessDetails[field as keyof BusinessDetails] !== previousBusinessDetails[field as keyof BusinessDetails]
      ));
      
      // Industry section changes
      const industryFields = ['industry'];
      setIndustryHasChanges(industryFields.some(field => 
        businessDetails[field as keyof BusinessDetails] !== previousBusinessDetails[field as keyof BusinessDetails]
      ));
    }
  }, [businessDetails, formSection, previousBusinessDetails]);
  
  // Simplify the conditions for showing sections
  const showAddressSection = basicInfoEdited;
  const showIndustrySection = addressEdited && showAddressSection;
  const showSubmitButton = industryEdited && showIndustrySection;
  
  // Update this to check for actual completion rather than changes
  const isSubmitEnabled = basicInfoComplete && addressComplete && industryComplete && 
                          basicInfoEdited && addressEdited && industryEdited;
  
  const resetFormSection = () => {
    setFormSection('search');
    setBasicInfoEdited(false);
    setAddressEdited(false);
    setIndustryEdited(false);
    setBasicInfoHasChanges(false);
    setAddressHasChanges(false);
    setIndustryHasChanges(false);
    setPreviousBusinessDetails({...businessDetails});
  };

  const saveBasicInfo = () => {
    if (basicInfoComplete) {
      setBasicInfoEdited(true);
      setBasicInfoHasChanges(false);
      // Save current state for future change detection
      setPreviousBusinessDetails(prev => ({...prev, ...businessDetails}));
    }
  };

  const saveAddress = () => {
    if (addressComplete) {
      setAddressEdited(true);
      setAddressHasChanges(false);
      // Save current state for future change detection
      setPreviousBusinessDetails(prev => ({...prev, ...businessDetails}));
    }
  };

  const saveIndustry = () => {
    if (industryComplete) {
      setIndustryEdited(true);
      setIndustryHasChanges(false);
      // Save current state for future change detection
      setPreviousBusinessDetails(prev => ({...prev, ...businessDetails}));
    }
  };

  return {
    formSection,
    setFormSection,
    basicInfoComplete,
    addressComplete,
    industryComplete,
    basicInfoEdited,
    addressEdited,
    industryEdited,
    showAddressSection,
    showIndustrySection,
    showSubmitButton,
    isSubmitEnabled,
    resetFormSection,
    saveBasicInfo,
    saveAddress,
    saveIndustry,
    // Add the new change tracking state to the return value
    basicInfoHasChanges,
    addressHasChanges,
    industryHasChanges
  };
};
